/**
 * MetricCardHeader — stacked KPI header (Directive §2.A).
 *
 * Renders a single unified rectangle:
 *   • Eyebrow / title row (Inter caption 12px, muted)
 *   • Primary KPI value  (Inter Bold 24px, proportional-nums)
 *   • Inline telemetry to the right of the value:
 *       - MiniSparkline SVG   (w-16 h-6, stroke 1.75px)
 *       - Delta badge         (data-scale palette, never raw red/green)
 *
 * Accessible Data Scale:
 *   • positive  → Low-Tilt Blue    (#2563EB)  (disciplined growth)
 *   • negative  → High-Tilt Orange (#EA580C)  (risk spike)
 *   • neutral   → Neutral Gray     (#6B7280)  (baseline)
 */
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { signTone } from "@/lib/numberColor";

/* ── MiniSparkline ────────────────────────────────────────────────
 * Deterministic SVG polyline from a numeric series. Renders in
 * data.blue by default; caller may override with `tone`.
 * ---------------------------------------------------------------- */
export function MiniSparkline({
    data = [],
    width = 64,
    height = 24,
    tone = "blue",
    className = "",
    testid = "mini-sparkline",
}) {
    if (!Array.isArray(data) || data.length < 2) {
        return (
            <svg width={width} height={height} className={className} data-testid={testid} aria-hidden="true" />
        );
    }
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = width / (data.length - 1);
    const pts = data
        .map((v, i) => {
            const x = i * stepX;
            const y = height - ((v - min) / range) * height;
            return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(" ");
    // Sept 2026 · global number-coloring convention — green=up/red=down,
    // replacing the prior blue/orange "accessible data scale".
    const colour = tone === "orange" ? "hsl(var(--danger))" : tone === "gray" ? "hsl(var(--zero))" : "hsl(var(--success))";
    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className={className}
            data-testid={testid}
            aria-hidden="true"
        >
            <polyline
                fill="none"
                stroke={colour}
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={pts}
            />
        </svg>
    );
}

/* ── DeltaBadge ───────────────────────────────────────────────────
 * Sept 2026 · global number-coloring convention: positive → green,
 * negative → red, flat/zero → grey (was previously blue/orange/gray
 * per the old "accessible data scale" Directive §2.A — superseded).
 * ---------------------------------------------------------------- */
export function DeltaBadge({ value, format = "percent", className = "", testid = "delta-badge" }) {
    const num = typeof value === "number" ? value : parseFloat(value);
    const isNaN = Number.isNaN(num);
    const tone = isNaN ? "zero" : signTone(num, format === "percent" ? 2 : 0);
    const styles = {
        pos:  { bg: "hsl(var(--success) / 0.10)", fg: "hsl(var(--success))", Icon: ArrowUpRight },
        neg:  { bg: "hsl(var(--danger) / 0.10)",   fg: "hsl(var(--danger))",  Icon: ArrowDownRight },
        zero: { bg: "hsl(var(--zero) / 0.10)",     fg: "hsl(var(--zero))",    Icon: Minus },
    }[tone];
    const Icon = styles.Icon;
    const abs = isNaN ? "—" : Math.abs(num);
    const display = format === "percent"
        ? isNaN ? "—" : `${abs.toFixed(2)}%`
        : format === "currency"
            ? isNaN ? "—" : abs.toLocaleString(undefined, { maximumFractionDigits: 2 })
            : String(abs);

    return (
        <span
            data-testid={testid}
            data-tone={tone}
            className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 font-heading text-ui-med ${className}`}
            style={{ background: styles.bg, color: styles.fg, fontFeatureSettings: '"pnum" 1, "zero" 0' }}
        >
            <Icon width={14} height={14} strokeWidth={1.75} />
            {display}
        </span>
    );
}

/* ── MetricCardHeader ─────────────────────────────────────────────
 * The one composable header for every dashboard card.
 *
 * Props:
 *   title      — text label (Inter caption uppercase)
 *   icon       — Lucide icon component (rendered in an IconChip caller-side)
 *   value      — primary KPI (string or number)
 *   suffix     — optional unit ("KES", "%", …)
 *   spark      — number[] for MiniSparkline (optional)
 *   sparkTone  — "blue" | "orange" | "gray"
 *   delta      — number for DeltaBadge (optional)
 *   deltaFormat— "percent" | "currency" | "raw"
 *   trailing   — custom right-hand slot (e.g. CardMicroTabs), overrides delta/spark
 * ---------------------------------------------------------------- */
export default function MetricCardHeader({
    title,
    subtitle,
    icon: LeadIcon,
    value,
    suffix,
    spark,
    sparkTone,
    delta,
    deltaFormat = "percent",
    trailing,
    className = "",
    testid,
}) {
    return (
        <div
            data-testid={testid || "metric-card-header"}
            className={`mb-4 density-compact:mb-2 ${className}`}
        >
            <div className="flex items-start justify-between gap-3 min-w-0">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 text-caption uppercase tracking-[0.15em] text-muted-foreground">
                        {LeadIcon && (
                            <LeadIcon width={14} height={14} strokeWidth={1.75} className="shrink-0 text-brand-green" />
                        )}
                        <span className="truncate">{title}</span>
                    </div>
                    <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
                        <span
                            className="font-heading font-bold text-h2 density-compact:text-[20px] leading-tight text-[#0F172A] dark:text-[#F8FAFC] whitespace-nowrap"
                            style={{ fontFeatureSettings: '"pnum" 1, "zero" 0' }}
                            data-testid={testid ? `${testid}-value` : "metric-card-value"}
                        >
                            {value}
                        </span>
                        {suffix && (
                            <span className="text-caption font-heading text-[#475569] dark:text-[#94A3B8] uppercase tracking-wider whitespace-nowrap">
                                {suffix}
                            </span>
                        )}
                    </div>
                    {subtitle && (
                        <div className="mt-1 text-caption font-body text-[#475569] dark:text-[#94A3B8] break-words">
                            {subtitle}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {trailing || (
                        <>
                            {Array.isArray(spark) && spark.length >= 2 && (
                                <MiniSparkline data={spark} tone={sparkTone} />
                            )}
                            {typeof delta !== "undefined" && delta !== null && (
                                <DeltaBadge value={delta} format={deltaFormat} />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
